import { Button, Pagination, Segmented } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Command, Segments } from "./MinerRowMoreService";
import PanelWithTable, { PanelTableRow } from "./PanelWithTable";
import useTypedRequest, { castResponse } from "@utils/hooks/useTypedRequest";
import { SegmentedValue } from "antd/es/segmented";
import { PostRequest } from "@services/http/BaseRequest";
import { RecordsResponse } from "wl-net";
import W2UiFilterBuilder from "@services/w2ui";
import { SearchItem } from "@services/w2ui/w2const";
import moment from "moment";


export const PAGE_SIZE = 20;

enum CommandStatus {
  Unknown = 0,
  New = 1,
  Sending = 2,
  Pending = 3,
  Done = 4,
  Error = 5,
  Unavailable = 6
}

const COMMAND_STATUS_MESSAGE_IDS: Record<CommandStatus, string> = {
  [CommandStatus.Unknown]: "command.status.unknown",
  [CommandStatus.New]: "command.status.new",
  [CommandStatus.Sending]: "command.status.sending",
  [CommandStatus.Pending]: "command.status.pending",
  [CommandStatus.Done]: "command.status.done",
  [CommandStatus.Error]: "command.status.error",
  [CommandStatus.Unavailable]: "command.status.unavailable",
};

// type CommandStatus = 1 | 4 | 5; // 1 - успех, 4 - предупреждение, 5 - ошибка

// Тип для команды
//type CommandType = 1 | 2 | 4 | 5 | 6 | 7;

interface MinerCommandRecord {
  id: number;
  miner_id: number;
  command: number;
  status: CommandStatus;
  data: string;
  timestamp: number;
  name: string;
  updated: string;
}

const GetMinerCurrentCommands = {
  makeRequest(minerId: number, page: number) {
    const params = new W2UiFilterBuilder()
      .addSearch(SearchItem.intIs("miner_id", minerId))
      .setLimit(PAGE_SIZE)
      .setOffset((page - 1) * PAGE_SIZE)
      .build()
      .stringParams;
    return new PostRequest("/admin/control/commands/current/list", params);
  },
  makeResponse: castResponse<RecordsResponse<MinerCommandRecord>>,
}

const GetMinerHistoryCommands = {
  makeRequest(minerId: number, page: number) {
    const params = new W2UiFilterBuilder()
      .addSearch(SearchItem.intIs("miner_id", minerId))
      .setLimit(PAGE_SIZE)
      .setOffset((page - 1) * PAGE_SIZE)
      .build()
      .stringParams;
    return new PostRequest("/admin/control/commands/history/list", params);
  },
  makeResponse: castResponse<RecordsResponse<MinerCommandRecord>>,
}
/*
export const getMinerErrorsList = {
  makeRequest(id: number) {
    const builder = new W2UiFilterBuilder();
    builder.setSearch([SearchItem.intIs("id", id)]);
    return new PostRequest(
      "/admin/control/errors/list",
      builder.build().stringParams
    );
  },
  makeResponse: castResponse<RecordsResponse<IMinerErrors>>,
}
*/

type Pager = { page: number; pageSize: number; total: number };

function useControl(minerId: number) {

  const [isLoading, setLoading] = useState(false);
  const { formatMessage } = useIntl();
  const { request: getCurrentCommands } = useTypedRequest(GetMinerCurrentCommands);
  const { request: getHistoryCommands } = useTypedRequest(GetMinerHistoryCommands);
  const [currentSegment, setCurrentSegment] = useState<Segments>(Segments.CURRENT);
  //const [pager, setPager] = useState<Pager>({ page: 1, pageSize: 20, total: 0 });
  const [rows, setRows] = useState<PanelTableRow[]>([]);
  const [page, setPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);


  const onSegmentChanged = (value: SegmentedValue) => {
    if (value === Segments.CURRENT) {
      setCurrentSegment(Segments.CURRENT);
    }
    if (value === Segments.HISTORY) {
      setCurrentSegment(Segments.HISTORY);
    }
  }

  const updateRows = useCallback(async (segment: Segments) => {
    const handlers = {
      [Segments.CURRENT]: () => getCurrentCommands(minerId, page),
      [Segments.HISTORY]: () => getHistoryCommands(minerId, page),
    };
    setLoading(true);
    try {
      const response = await handlers[segment]();
      if (response && response.Status === "success") {
        setTotal(response.Total);
        const rows = response.Records.map((record) => {
          //const parsedData = parseMinerErrorData(record.data);
          // "Команда",
          // "Статус",
          // "Данные",
          // "Время"
          return {
            values: [
              Command.toString(record.command),
              formatMessage({ id: COMMAND_STATUS_MESSAGE_IDS[record.status] }),
              record.data,
              // 31.07.2026 19:56:11
              moment(record.timestamp * 1000).format("DD.MM.YYYY HH:mm:ss")
              // record.updated,
            ],
          };
        });
        setRows(rows);
      }
    } finally {
      setLoading(false);
    }
  }, [minerId, page]);

  const onPageChange = (page: number, pageSize: number) => {
    setPage(page);
  }

  useEffect(() => {
    updateRows(currentSegment);
  }, [currentSegment]);


  return {
    page,
    pageSize: PAGE_SIZE,
    total,
    currentSegment,
    rows,
    isLoading,
    onSegmentChanged,
    updateRows,
    onPageChange,
  };
}

function MinerCommandHistory({ minerId }: { minerId: number }) {

  const control = useControl(minerId);

  return (<>
    {/* <Button onClick={addFake}>Add Fake</Button> */}
    <PanelWithTable
      caption="Команды"
      isLoading={control.isLoading}
      segments={[
        { label: "текущие", value: Segments.CURRENT },
        { label: "история", value: Segments.HISTORY }
      ]}
      onSegmentChanged={control.onSegmentChanged}
      columns={[
        "Команда",
        "Статус",
        "Данные",
        "Время"
      ]}
      rows={control.rows}
    />
    <Pagination size="small"
      defaultCurrent={1}
      current={control.page}
      total={control.total}
      pageSize={PAGE_SIZE}
      onChange={control.onPageChange}
    />
  </>);
}

export default MinerCommandHistory;
